import { CloseOutlined, LogoutOutlined } from '@ant-design/icons';
import AlarmNotification from '@components/AlarmNotification/AlarmNotification';
import FloatingButton from '@components/FloatingButton';
import PagesHeader from '@components/PagesHeader';
import PagesMenu from '@components/PagesMenu';
import PortalSpinner from '@components/PortalSpinner';
import SatisfactionSurvey from '@components/SatisfactionSurvey';
import SelectContext from '@components/SelectContext';
import keycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { changeDocumentTitle } from '@utils/changeDocumentTitle';
import { changeFavicon } from '@utils/changeFavicon';
import { toISOStringWithOffset } from '@utils/formatters';
import isAValidPathname from '@utils/isAValidPathname';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import { Alert, Button, Drawer, Layout, message, Popover } from 'antd';
import moment, { Moment } from 'moment';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { NotificationItem, useNotifications } from '@hooks/useNotifications';
import {
  AlarmNotificationContent,
  AlarmData,
} from '@components/AlarmNotificationContent/AlarmNotificationContent';
import { AlarmNotificationActionButtons } from '@components/AlarmNotificationActionButtons/AlarmNotificationActionButtons';
import RoleChecker from '@components/RoleChecker';
import {
  List as VirtuList,
  CellMeasurer,
  CellMeasurerCache,
  AutoSizer,
} from 'react-virtualized';
import LanguageSelector from '../components/LanguageSelector';
import * as S from './styles';
import ConsumptionAlertNotify from './Insights/Consumption/ConsumptionAlerts/ConsumptionAlertNotify';

const { Sider: Sidebar, Content, Footer } = Layout;

const MOBILE_GAP = 8;
const TOTAL_HEIGHT = 420;
const HEADER_HEIGHT = 56;

const mobileCache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 110,
  minHeight: 90,
});

const Main: FC = () => {
  const { t } = useTranslation(['main', 'alarms-notifications']);
  const { keycloak } = useContext(keycloakContext);
  const {
    isMot,
    effectiveRoles,
    isContextSwitch,
    allowedPaths,
    isAlgarDomain,
    domainCustomerName,
    mainColor,
    isMotEnterprise,
    whatsapp,
    whatsappMessage,
    personalizationCompleted,
    userLogged,
    isRoot,
    firstMotProduct,
    userPreferences: { setUserAttributes, getUserAttributes },
    customer: {
      customerLoggedName,
      customerLogged,
      isCustomerFinanciallyBlocked,
    },
  } = useContext(SessionContext);

  const { pathname } = useLocation();
  const isPageHome = pathname === '/';
  const navigate = useNavigate();
  const isCustomer = customerLogged?.type === 'CUSTOMER';

  const CONNECTIVITY_PRODUCTS = [
    ApplicationsConstants.MULTI_CONNECT,
    ApplicationsConstants.CONNECT_ONE,
  ];
  const hasConnectivityProduct = customerLogged?.listContractTypes?.some(
    (type: any) => CONNECTIVITY_PRODUCTS.includes(type.toLowerCase()),
  );

  const [alreadyViewEvents, setAlreadyViewEvents] = useState(true);
  const [eventsAlertsData, setEventsAlertsData] = useState(null);
  const [showPagesMenu, setShowPagesMenu] = useState(false);

  const [mobileNotificationPopoverOpen, setMobileNotificationPopoverOpen] =
    useState(false);
  const mobileNotificationContentRef = useRef<HTMLDivElement>(null);
  const [mobileHasScrollbar, setMobileHasScrollbar] = useState(false);
  const [listKey, setListKey] = useState(0);

  const {
    filteredNotifications,
    isNotificationsLoading,
    loadingNotifications,
    handleRemoveNotification,
    handleNotificationClick,
    handleNavigateToAlarmConfigPage,
    handleNavigateToAlarmPage,
    formatAlarmType,
  } = useNotifications();

  useEffect(() => {
    mobileCache.clearAll();
  }, [filteredNotifications]);

  const handleMobileNavigateToAlarmConfigPage = useCallback(() => {
    handleNavigateToAlarmConfigPage();
    setMobileNotificationPopoverOpen(false);
    setShowPagesMenu(false);
  }, [handleNavigateToAlarmConfigPage]);

  const handleMobileNavigateToAlarmPage = useCallback(() => {
    handleNavigateToAlarmPage();
    setMobileNotificationPopoverOpen(false);
    setShowPagesMenu(false);
  }, [handleNavigateToAlarmPage]);

  const MobileNotificationRowItem = React.memo(
    (props: {
      alarmData: AlarmData;
      isRemoving: boolean;
      onNavigate: () => void;
      onRemove: () => Promise<void> | void;
    }) => {
      const { alarmData, isRemoving, onNavigate, onRemove } = props;
      return (
        <div style={{ paddingRight: 8 }}>
          <AlarmNotificationContent
            alarmData={alarmData}
            isToast={false}
            navigateToAlarmPage={onNavigate}
            onRemove={onRemove}
            isRemoving={isRemoving}
            showRemoveButton
          />
        </div>
      );
    },
  );

  const mobileNotificationContent = useMemo(() => {
    const getTipoDisparo = (disparo: string) => {
      if (disparo === 'CRITICAL') return 'critical';
      if (disparo === 'OK') return 'ok';
      if (disparo === 'CRIT') return 'crit';
      return 'recovery';
    };

    const listItems = filteredNotifications.map(
      (notification: NotificationItem, index: number) => ({
        label: notification.nomeAlarme,
        value: index,
        alarmData: {
          ...notification,
          tipoAlarme: formatAlarmType(
            notification.tipoAlarme,
            notification.tipoAlarmeOriginal,
          ),
          tipoDisparo: getTipoDisparo(notification.disparo),
        } as AlarmData,
        isRemoving: loadingNotifications.has(notification.idAlarme),
        onNavigate: () =>
          handleNotificationClick(notification, () => {
            setMobileNotificationPopoverOpen(false);
            setShowPagesMenu(false);
          }),
        onRemove: async () => {
          await handleRemoveNotification(
            notification.idAlarme,
            notification.timeId,
          );
        },
      }),
    );

    const LIST_HEIGHT = Math.max(
      180,
      TOTAL_HEIGHT - HEADER_HEIGHT - MOBILE_GAP,
    );

    return (
      <S.NotificationContent
        ref={mobileNotificationContentRef}
        className={`${mobileHasScrollbar ? 'has-scrollbar' : ''} mobile`}
        style={{
          width: '100%',
          height: filteredNotifications.length > 0 ? TOTAL_HEIGHT : 60,
          padding: 0,
          overflow: 'visible',
        }}
      >
        {filteredNotifications.length > 0 ? (
          <>
            <div
              style={{
                height: HEADER_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                boxSizing: 'border-box',
              }}
            >
              <AlarmNotificationActionButtons
                isEmpty={false}
                navigateToAlarmConfigPage={
                  handleMobileNavigateToAlarmConfigPage
                }
                navigateToAlarmPage={handleMobileNavigateToAlarmPage}
              />
            </div>

            <div
              style={{
                height: LIST_HEIGHT,
                width: '100%',
                paddingBottom: MOBILE_GAP,
                boxSizing: 'border-box',
              }}
            >
              {/* @ts-ignore */}
              <AutoSizer disableHeight>
                {({ width }) => {
                  const listKeyWithWidth = `${listKey}-${Math.round(width)}`;
                  return (
                    // @ts-ignore
                    <VirtuList
                      key={listKeyWithWidth}
                      height={LIST_HEIGHT}
                      width={width}
                      rowCount={listItems.length}
                      deferredMeasurementCache={mobileCache}
                      rowHeight={mobileCache.rowHeight}
                      estimatedRowSize={118}
                      overscanRowCount={4}
                      scrollToIndex={0}
                      style={{ outline: 'none' }}
                      noRowsRenderer={() => (
                        <div style={{ padding: 16 }}>Sem itens</div>
                      )}
                      rowRenderer={({ key, index, style, parent }) => {
                        const item = listItems[index];
                        if (!item) return null;
                        return (
                          // @ts-ignore
                          <CellMeasurer
                            key={key}
                            cache={mobileCache}
                            parent={parent}
                            columnIndex={0}
                            rowIndex={index}
                          >
                            {({ registerChild }) => (
                              <div
                                ref={registerChild as React.Ref<HTMLDivElement>}
                                style={{
                                  ...style,
                                  paddingBottom: MOBILE_GAP,
                                  boxSizing: 'border-box',
                                }}
                                className="virtual-list-item"
                              >
                                <MobileNotificationRowItem {...item} />
                              </div>
                            )}
                          </CellMeasurer>
                        );
                      }}
                    />
                  );
                }}
              </AutoSizer>
            </div>
          </>
        ) : (
          <AlarmNotificationActionButtons
            isEmpty
            navigateToAlarmConfigPage={handleMobileNavigateToAlarmConfigPage}
            navigateToAlarmPage={handleMobileNavigateToAlarmPage}
          />
        )}
      </S.NotificationContent>
    );
  }, [
    filteredNotifications,
    mobileHasScrollbar,
    handleMobileNavigateToAlarmConfigPage,
    handleMobileNavigateToAlarmPage,
    formatAlarmType,
    loadingNotifications,
    handleNotificationClick,
    handleRemoveNotification,
    listKey,
    MobileNotificationRowItem,
  ]);

  useEffect(() => {
    changeDocumentTitle({ t, pathname, customerLoggedName, isAlgarDomain });
  }, [customerLoggedName, t, pathname, isAlgarDomain]);

  useEffect(() => {
    setShowPagesMenu(isAValidPathname(pathname, allowedPaths));
  }, [pathname, allowedPaths]);

  useEffect(() => {
    if (mainColor) {
      changeFavicon(mainColor);
    }
  }, [isAlgarDomain, mainColor]);

  const togglePagesMenu = useCallback(() => setShowPagesMenu(v => !v), []);

  const onLogout = useCallback(() => {
    if (keycloak?.logout) keycloak.logout();
  }, [keycloak]);

  const hasRole = useCallback(
    (role: RolesTypes) => effectiveRoles?.portal?.includes(role),
    [effectiveRoles],
  );

  const fetchEventsAlertsData = useCallback(
    async (dateToSearch: Moment, defaultLocal = null) => {
      try {
        const dateFromFormatted = toISOStringWithOffset(dateToSearch);
        const response = await api.get('/service-proxy/alert-events/summary', {
          params: { dateFrom: dateFromFormatted },
        });
        if (response.data?.items?.length > 0) {
          setAlreadyViewEvents(false);
          setUserAttributes(
            { alreadyViewEvents: 'false', dateFrom: String(moment()) },
            defaultLocal,
          );

          response.data.items.forEach((e: any) => {
            const closeMessage = () => message.destroy();
            message.open({
              content: (
                <div style={{ position: 'relative', paddingRight: '30px' }}>
                  <Button
                    icon={<CloseOutlined />}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      fontSize: '14px',
                      color: '#999',
                      padding: 0,
                    }}
                    type="link"
                    onClick={closeMessage}
                  />
                  <div>
                    <strong>
                      {`${t(
                        `${
                          e.totalAlerts > 1
                            ? 'alerts:titles.new-event_plural'
                            : 'alerts:titles.new-event'
                        }`,
                        { count: e.totalAlerts },
                      )} ${t(`alerts:eventType.${e.eventType}`)}`}
                    </strong>
                  </div>
                  <div>
                    <Button
                      type="link"
                      onClick={() => {
                        closeMessage();
                        setAlreadyViewEvents(true);
                        setUserAttributes(
                          { alreadyViewEvents: 'true' },
                          defaultLocal,
                        );
                        e.eventType === 'CONSUMPTION'
                          ? navigate(
                              `${firstMotProduct?.toLocaleLowerCase()}${
                                firstMotProduct?.toLocaleLowerCase() !==
                                ApplicationsConstants.MOT_PAY
                                  ? '/mobile-network'
                                  : ''
                              }/assets/inventory?lastAlertDateFrom=${toISOStringWithOffset(
                                response.data.dateFrom,
                                3,
                              )}&showContracted=${
                                isCustomer ? 'true' : 'false'
                              }`,
                            )
                          : navigate(
                              `${firstMotProduct?.toLocaleLowerCase()}${
                                firstMotProduct?.toLocaleLowerCase() !==
                                ApplicationsConstants.MOT_PAY
                                  ? '/mobile-network'
                                  : ''
                              }/financial/contracts?listingType=active&alertDateFrom=${toISOStringWithOffset(
                                response.data.dateFrom,
                                3,
                              )}&showContracted=${
                                isCustomer ? 'true' : 'false'
                              }`,
                            );
                      }}
                    >
                      {e.eventType === 'CONSUMPTION'
                        ? t('alerts:titles.view-event')
                        : t('alerts:titles.view-event-contracts')}
                    </Button>
                    <Button
                      type="link"
                      onClick={() => {
                        closeMessage();
                        setAlreadyViewEvents(true);
                        setUserAttributes(
                          { alreadyViewEvents: 'true' },
                          defaultLocal,
                        );
                        navigate(
                          `${firstMotProduct?.toLocaleLowerCase()}${
                            firstMotProduct?.toLocaleLowerCase() !==
                            ApplicationsConstants.MOT_PAY
                              ? '/mobile-network'
                              : ''
                          }/alerts/events`,
                        );
                      }}
                    >
                      {t('alerts:titles.view-event-others')}
                    </Button>
                  </div>
                </div>
              ),
              duration: 30,
            });
          });
        } else {
          setAlreadyViewEvents(true);
          setUserAttributes(
            { alreadyViewEvents: 'true', dateFrom: String(moment()) },
            defaultLocal,
          );
        }
        setEventsAlertsData(response.data);
      } catch (err) {
        console.error('Erro ao buscar eventos/alertas:', err);
      }
    },
    [setUserAttributes, t, navigate, firstMotProduct, isCustomer],
  );

  useEffect(() => {
    if (hasRole(rolesConstants.VIEW_ALERTS) && !isRoot && !!firstMotProduct) {
      const storedDateFrom = getUserAttributes()?.dateFrom;
      const initialDateFrom = storedDateFrom
        ? moment(storedDateFrom)
        : moment().subtract(1, 'days');

      if (!storedDateFrom) {
        setUserAttributes({ dateFrom: String(initialDateFrom) });
      }

      fetchEventsAlertsData(initialDateFrom);

      const interval = setInterval(() => {
        const preferences = localStorage.getItem('userPreferences');
        const user = `user_${userLogged?.user_id}`;
        const userPreferences = preferences
          ? JSON.parse(preferences)?.[user]
          : null;
        fetchEventsAlertsData(
          userPreferences?.dateFrom ?? initialDateFrom,
          userPreferences,
        );
      }, 300000);

      return () => clearInterval(interval);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstMotProduct]);

  if (isContextSwitch || !effectiveRoles || !personalizationCompleted) {
    return (
      <PortalSpinner>
        {isContextSwitch && (
          <div style={{ marginTop: 12 }}>
            <p className="logo-container-context-text">
              {t('context-switch.spinner.description')}
            </p>
          </div>
        )}
      </PortalSpinner>
    );
  }

  return (
    <S.LayoutContainer isPageHome={isPageHome}>
      {isAlgarDomain && <SatisfactionSurvey />}
      <Sidebar
        className={`main-sidebar hide-on-mobile ${
          isPageHome && 'main-sidebar-collapsed'
        }`}
        width="220"
        collapsed={!showPagesMenu}
        collapsedWidth={0}
      >
        <PagesMenu
          eventsAlertsData={eventsAlertsData}
          alreadyViewEvents={alreadyViewEvents}
          setAlreadyViewEvents={setAlreadyViewEvents}
          setUserAttributes={setUserAttributes}
        />
      </Sidebar>

      <Layout style={{ height: '100vh' }}>
        <PagesHeader
          showPagesMenu={showPagesMenu}
          togglePagesMenu={togglePagesMenu}
        />

        <Drawer
          placement="left"
          onClose={togglePagesMenu}
          closeIcon={<CloseOutlined />}
          open={showPagesMenu}
          className="main-header-mobile-drawer hide-on-desktop"
          // @ts-ignore
          getContainer={() => document.getElementById('drawer-container')}
        >
          <PagesMenu
            eventsAlertsData={eventsAlertsData}
            alreadyViewEvents={alreadyViewEvents}
            setAlreadyViewEvents={setAlreadyViewEvents}
            setUserAttributes={setUserAttributes}
          />
          <div>
            <div
              style={{
                textAlign: 'center',
                alignItems: 'center',
                display: 'flex',
                flexFlow: 'column',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <SelectContext width="100%" className="select-context-mobile" />

              <Popover
                trigger="click"
                content={mobileNotificationContent}
                placement="bottomRight"
                overlayStyle={{ maxWidth: '92vw', width: 'auto' }}
                destroyTooltipOnHide
                getPopupContainer={
                  trigger => trigger.parentElement as HTMLElement
                  // eslint-disable-next-line react/jsx-curly-newline
                }
                open={mobileNotificationPopoverOpen}
                onOpenChange={open => {
                  setMobileNotificationPopoverOpen(open);
                  if (open) {
                    mobileCache.clearAll();
                    setListKey(k => k + 1);
                    requestAnimationFrame(() => {
                      const el = mobileNotificationContentRef.current;
                      if (el)
                        setMobileHasScrollbar(
                          el.scrollHeight > el.clientHeight,
                        );
                    });
                  }
                }}
              >
                <div style={{ cursor: 'pointer' }}>
                  <AlarmNotification
                    count={filteredNotifications.length}
                    isMobileMenu
                    isLoading={isNotificationsLoading}
                  />
                </div>
              </Popover>

              <LanguageSelector />
              <Button
                type="link"
                size="large"
                onClick={onLogout}
                icon={<LogoutOutlined />}
              />
            </div>
          </div>
        </Drawer>

        {isCustomerFinanciallyBlocked && isMot && (
          <Alert
            message={t('financial-block-msg.title').toUpperCase()}
            description={t('financial-block-msg.details')}
            type="error"
            showIcon
            style={{ borderRadius: 0 }}
          />
        )}

        <Content className="main-content">
          <Outlet />
          {isMotEnterprise && whatsapp && (
            <FloatingButton
              icon={<AiOutlineWhatsApp />}
              title={t('personalization.whatsapp-button')}
              onClick={() => {
                window.open(
                  `https://api.whatsapp.com/send?phone=${whatsapp}&text=${
                    whatsappMessage ?? t('whatsapp-button.text')
                  }`,
                );
              }}
            />
          )}
          {!isRoot && hasConnectivityProduct && (
            <RoleChecker role={rolesConstants.VIEW_CONSUMPTION_ALERTS}>
              <ConsumptionAlertNotify />
            </RoleChecker>
          )}
          <Footer className="main-footer">
            {`© Copyright ${new Date().getFullYear()} ${
              isAlgarDomain ? 'Algar' : domainCustomerName
            } - ${t('all-right-reserved')}`}
          </Footer>
        </Content>
      </Layout>
    </S.LayoutContainer>
  );
};

export default Main;
